// PRINT-6608 (PIP-3131 Class C): pure bounds-clamp cascade used by
// ImageSize.tsx manual-input aspect reconciliation. Mirrors the auto-sizing
// fix shipped in PR #57510 / useDesignLabArtworkAutoSizing.
//
// Given an anchored axis (the one the user just changed) and the target
// aspect ratio, derive the partner axis. If clamping the partner to its
// surface bound mutates the value, re-derive the anchored axis from the
// clamped partner so the final pair preserves aspect ratio. Without this
// cascade, manual input near the surface bounds persists an off-aspect pair.

export type Bounds = {
  minWidthInches: number;
  maxWidthInches: number;
  minHeightInches: number;
  maxHeightInches: number;
};

export type ReconciledSize = {
  width: number;
  height: number;
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

const round2 = (value: number) => Math.round(value * 100) / 100;

// Reconcile after a width edit. Returns the post-cascade {width, height}
// always within bounds. The edited axis is clamped first; the partner is
// derived against the aspect line; if the partner saturates a bound, the
// edited axis is re-derived from the clamped partner so the final pair
// preserves aspect ratio AND stays within bounds.
export function reconcileFromWidth(width: number, ratio: number, bounds: Bounds): ReconciledSize {
  const clampedWidth = clamp(round2(width), bounds.minWidthInches, bounds.maxWidthInches);
  const derivedHeight = round2(clampedWidth / ratio);
  const newHeight = clamp(derivedHeight, bounds.minHeightInches, bounds.maxHeightInches);
  if (newHeight === derivedHeight) {
    return { width: clampedWidth, height: newHeight };
  }
  const correctedWidth = clamp(
    round2(newHeight * ratio),
    bounds.minWidthInches,
    bounds.maxWidthInches
  );
  return { width: correctedWidth, height: newHeight };
}

// Reconcile after a height edit. Symmetric.
export function reconcileFromHeight(height: number, ratio: number, bounds: Bounds): ReconciledSize {
  const clampedHeight = clamp(round2(height), bounds.minHeightInches, bounds.maxHeightInches);
  const derivedWidth = round2(clampedHeight * ratio);
  const newWidth = clamp(derivedWidth, bounds.minWidthInches, bounds.maxWidthInches);
  if (newWidth === derivedWidth) {
    return { width: newWidth, height: clampedHeight };
  }
  const correctedHeight = clamp(
    round2(newWidth / ratio),
    bounds.minHeightInches,
    bounds.maxHeightInches
  );
  return { width: newWidth, height: correctedHeight };
}

// PRINT-6608 (PIP-3131 Class D): reconcile a 2-axis drag-resize. Anchors on
// the axis that the user "extended more" (max relative scale vs surface
// bound), then reconciles the partner via the aspect line.
//
// bounds may carry maxAspectRatioByWidth (artwork natural aspect, set by
// calculateDimensions.ts); if absent or zero, the reconciler is a no-op and
// returns the input rounded to 2dp. This preserves behavior on placements
// whose surface metadata hasn't loaded yet.
export type ResizeBounds = Bounds & { maxAspectRatioByWidth?: number };

export function reconcileResize(
  widthInches: number,
  heightInches: number,
  bounds: ResizeBounds | undefined
): ReconciledSize {
  const fallback = { width: round2(widthInches), height: round2(heightInches) };
  if (!bounds) return fallback;
  const ratio = bounds.maxAspectRatioByWidth;
  if (!ratio || !Number.isFinite(ratio) || ratio <= 0) return fallback;

  // Anchor on the axis that the user pushed closer to its surface bound.
  const widthScale = bounds.maxWidthInches > 0 ? widthInches / bounds.maxWidthInches : 0;
  const heightScale = bounds.maxHeightInches > 0 ? heightInches / bounds.maxHeightInches : 0;

  return widthScale >= heightScale
    ? reconcileFromWidth(widthInches, ratio, bounds)
    : reconcileFromHeight(heightInches, ratio, bounds);
}
